import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { getManagerOwner, onManagerModeChange } from "../lib/managerMode";
import { canManagerSeePage } from "../access/pageAccess";
import useTenantBranding from "../hooks/useTenantBranding";
import useTenantPageAccess from "../hooks/useTenantPageAccess";
import useTenantPlanKey from "../hooks/useTenantPlanKey";

function Icon({ name, size = 20, stroke = 1.8 }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    style: { flex: "0 0 auto" },
    stroke: "currentColor",
    strokeWidth: stroke,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  switch (name) {
    case "home":
      return (
        <svg {...common}><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/><path d="M9.5 21v-6h5v6"/></svg>
      );
    case "vault":
      return (
        <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M7 12h6"/><circle cx="15.5" cy="12" r="1.5"/></svg>
      );
    case "message":
      return (
        <svg {...common}><path d="M4 6h16v10H8l-4 4V6z"/></svg>
      );
    case "headset":
      return (
        <svg {...common}>
          <path d="M5 12a7 7 0 0 1 14 0v3" />
          <path d="M5 15v1a2 2 0 0 0 2 2h1.5v-5H7a2 2 0 0 0-2 2z" />
          <path d="M19 15v1a2 2 0 0 1-2 2h-1.5v-5H17a2 2 0 0 1 2 2z" />
          <path d="M12 19v1" />
        </svg>
      );
    case "legal":
      return (
        <svg {...common}><path d="M7 3h7l5 5v13H7z"/><path d="M14 3v5h5"/></svg>
      );
    case "personal":
      return (
        <svg {...common}><path d="M12 21s-6.5-3.8-8.5-8.1C1.6 9 3.5 6 6.6 6c2.1 0 3.1 1.3 3.4 2 .3-.7 1.3-2 3.4-2 3.1 0 5 3 3.1 6.9C18.5 17.2 12 21 12 21z"/></svg>
      );
    case "search":
      return (
        <svg {...common}><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
      );
    case "contacts":
      return (
        <svg {...common}><circle cx="9" cy="8" r="3"/><path d="M14.5 20C14 17 11.7 15.5 9 15.5S4 17 3.5 20"/><circle cx="18" cy="10" r="2"/><path d="M21 20c-.2-1.6-1.4-3-3-3"/></svg>
      );
    case "admin":
      return (
        <svg {...common}><path d="M12 3l2 4 4 2-4 2-2 4-2-4-4-2 4-2 2-4z"/><path d="M4 19h16"/></svg>
      );
    default:
      return null;
  }
}

const PERSONAL_MENU = [
  { label: "Home", path: "/", icon: "home", pageKey: "home" },
  {
    label: "Account Managers",
    icon: "contacts",
    children: [
      { label: "Invite Managers", path: "/account-managers/invite", pageKey: "invite_managers" },
      { label: "Managed Accounts", path: "/account-managers/managed", pageKey: "managed_accounts_admin" },
    ],
  },
  {
    label: "Vaults",
    icon: "vault",
    children: [
      { label: "Password Vault", path: "/vaults/password-vault", pageKey: "password_vault" },
      { label: "Digital Platforms", path: "/vaults/digital-platforms", pageKey: "digital_platforms" },
      { label: "Devices", path: "/vaults/devices", pageKey: "devices" }
    ],
  },
  {
    label: "Messages",
    icon: "message",
    children: [
      { label: "Personal Messages", path: "/messages/personal-messages", pageKey: "personal_messages" },
      { label: "Time Vault", path: "/messages/time-vault", pageKey: "time_vault" },
      { label: "Last Goodbyes", path: "/messages/last-goodbyes", pageKey: "last_goodbyes" },
      { label: "Videos", path: "/messages/videos", pageKey: "videos" }
    ],
  },
  {
    label: "Legal",
    icon: "legal",
    children: [
      { label: "Important Documents", path: "/legal/important-documents", pageKey: "important_documents" },
      { label: "Advance Healthcare Directive", path: "/legal/advance-healthcare-directive", pageKey: "advance_healthcare_directive" },
      { label: "Secure E-Will", path: "/legal/secure-e-will", pageKey: "secure_e_will" },
      { label: "Pensions", path: "/legal/pensions", pageKey: "pensions" }
    ],
  },
  {
    label: "Personal",
    icon: "personal",
    children: [
      { label: "Organ Donation", path: "/personal/organ-donation", pageKey: "organ_donation" },
      { label: "Funeral Planning", path: "/personal/funeral-planning", pageKey: "funeral_planning" },
      { label: "Memory Lane", path: "/personal/memory-lane", pageKey: "memory_lane" },
      { label: "Belongings", path: "/personal/belongings", pageKey: "belongings" }
    ],
  },
  {
    label: "Support",
    icon: "headset",
    children: [
      { label: "Proof of Life", path: "/proof-of-life", pageKey: "proof_of_life" },
      { label: "Security", path: "/security", pageKey: "security" },
      { label: "Support Ticket", path: "/support", pageKey: "support_ticket" }
    ],
  },
  { label: "Contacts", icon: "contacts", path: "/contacts", pageKey: "contacts" },
];

// Nursing home staff accounts are work-only. Personal sections (Vaults, Legal,
// Messages, Time Vault) are deliberately excluded - a work login gets handed on
// when staff leave, so personal end-of-life data must never sit behind it.
const NURSING_HOME_MENU = [
  { label: "Home", path: "/", icon: "home", pageKey: "home" },
  { label: "Contacts", icon: "contacts", path: "/contacts", pageKey: "contacts" },
  {
    label: "Support",
    icon: "support",
    children: [
      { label: "Help Centre", path: "/support", pageKey: "support" },
    ],
  },
];

const BUSINESS_MENU = [
  { label: "Home", path: "/", icon: "home", pageKey: "home" },
  {
    label: "Account Managers",
    icon: "contacts",
    tenantGroupKey: "group_account_managers",
    children: [
      { label: "Invite Managers", path: "/account-managers/invite", pageKey: "invite_managers" },
      { label: "Managed Accounts", path: "/account-managers/managed", pageKey: "managed_accounts_admin" },
    ],
  },
  {
    label: "Vaults",
    icon: "vault",
    tenantGroupKey: "group_vaults",
    children: [
      { label: "Password Vault", path: "/vaults/password-vault", pageKey: "password_vault" },
      { label: "Digital Platforms", path: "/vaults/digital-platforms", pageKey: "digital_platforms" },
      { label: "Devices", path: "/vaults/devices", pageKey: "devices" }
    ],
  },
  {
    label: "Messages",
    icon: "message",
    tenantGroupKey: "group_messages",
    children: [
      { label: "Personal Messages", path: "/messages/personal-messages", pageKey: "personal_messages" },
      { label: "Time Vault", path: "/messages/time-vault", pageKey: "time_vault" },
      { label: "Last Goodbyes", path: "/messages/last-goodbyes", pageKey: "last_goodbyes" },
      { label: "Videos", path: "/messages/videos", pageKey: "videos" }
    ],
  },
  {
    label: "Legal",
    icon: "legal",
    tenantGroupKey: "group_legal",
    children: [
      { label: "Important Documents", path: "/legal/important-documents", pageKey: "important_documents" },
      { label: "Advance Healthcare Directive", path: "/legal/advance-healthcare-directive", pageKey: "advance_healthcare_directive" },
      { label: "Secure E-Will", path: "/legal/secure-e-will", pageKey: "secure_e_will" },
      { label: "Pensions", path: "/legal/pensions", pageKey: "pensions" }
    ],
  },
  {
    label: "Personal",
    icon: "personal",
    tenantGroupKey: "group_personal",
    children: [
      { label: "Organ Donation", path: "/personal/organ-donation", pageKey: "organ_donation" },
      { label: "Funeral Planning", path: "/personal/funeral-planning", pageKey: "funeral_planning" },
      { label: "Memory Lane", path: "/personal/memory-lane", pageKey: "memory_lane" },
      { label: "Belongings", path: "/personal/belongings", pageKey: "belongings" }
    ],
  },
  {
    label: "Support",
    icon: "headset",
    tenantGroupKey: "group_support",
    children: [
      { label: "Proof of Life", path: "/proof-of-life", pageKey: "proof_of_life" },
      { label: "Security", path: "/security", pageKey: "security" },
      { label: "Support Ticket", path: "/support", pageKey: "support_ticket" }
    ],
  },
  { label: "Contacts", icon: "contacts", path: "/contacts", pageKey: "contacts" },
];

function pathToPageKey(path) {
  const p = (path || "").trim();
  const clean = p === "/" ? "/" : p.replace(/\/+$/, "");
  switch (clean) {
    case "/":
      return "home";
    case "/contacts":
      return "contacts";
    case "/support":
      return "support_ticket";
    case "/legal/important-documents":
      return "important_documents";
    case "/legal/advance-healthcare-directive":
      return "advance_healthcare_directive";
    case "/personal/organ-donation":
      return "organ_donation";
    case "/personal/funeral-planning":
      return "funeral_planning";
    case "/legal/secure-e-will":
      return "secure_e_will";
    case "/legal/pensions":
      return "pensions";
    case "/vaults/password-vault":
      return "password_vault";
    case "/vaults/digital-platforms":
      return "digital_platforms";
    case "/vaults/devices":
      return "devices";
    case "/messages/personal-messages":
      return "personal_messages";
    case "/messages/time-vault":
      return "time_vault";
    case "/messages/last-goodbyes":
      return "last_goodbyes";
    case "/messages/videos":
      return "videos";
    case "/messages/legacy-message":
      return "legacy_message";
    case "/proof-of-life":
      return "proof_of_life";
    case "/security":
      return "security";
    case "/personal/memory-lane":
      return "memory_lane";
    case "/personal/belongings":
      return "belongings";
    case "/account-managers/invite":
      return "invite_managers";
    case "/account-managers/managed":
      return "managed_accounts_admin";
    default:
      return null;
  }
}

function isTenantAccessEnabled(access, key) {
  return access?.[key] !== false;
}

function filterMenuForTenant(items, tenantPageAccess) {
  const out = [];
  for (const item of items) {
    if (item.children && Array.isArray(item.children)) {
      const groupKey = item.tenantGroupKey || null;
      if (groupKey && !isTenantAccessEnabled(tenantPageAccess, groupKey)) {
        continue;
      }

      const kids = item.children.filter((child) => {
        const childKey = child.pageKey || pathToPageKey(child.path);
        return childKey ? isTenantAccessEnabled(tenantPageAccess, childKey) : true;
      });

      if (kids.length) out.push({ ...item, children: kids });
    } else if (item.path) {
      const k = item.pageKey || pathToPageKey(item.path);
      if (!k || isTenantAccessEnabled(tenantPageAccess, k)) out.push(item);
    } else {
      out.push(item);
    }
  }
  return out;
}

function filterMenuForManager(items, grant) {
  const out = [];
  for (const item of items) {
    if (item.children && Array.isArray(item.children)) {
      const kids = item.children.filter((c) => {
        const k = c.pageKey || pathToPageKey(c.path);
        return k ? canManagerSeePage(grant, k) : false;
      });
      if (kids.length) out.push({ ...item, children: kids });
    } else if (item.path) {
      const k = item.pageKey || pathToPageKey(item.path);
      if (k && canManagerSeePage(grant, k)) out.push(item);
    }
  }
  return out;
}

export default function Sidebar({ open = false, onClose, user, mode = "overlay" }) {
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({});
  const branding = useTenantBranding(user);
  const tenantPageAccess = useTenantPageAccess(user);
  const sidebarTenantId = String(
    user?.businessAccount?.tenantId ||
    user?.primaryTenantId ||
    user?.tenantId ||
    ""
  ).trim();
  const tenantPlanKey = useTenantPlanKey(sidebarTenantId);
  const residentRefCode = String(user?.refCode || "").trim();

  const [mm, setMm] = useState(() => getManagerOwner());
  const [grant, setGrant] = useState(null);

  useEffect(() => onManagerModeChange(setMm), []);

  useEffect(() => {
    if (!mm?.ownerUid || !mm?.managerUid) {
      setGrant(null);
      return;
    }
    const id = `${mm.ownerUid}_${mm.managerUid}`;
    const unsub = onSnapshot(
      doc(db, "grants", id),
      (snap) => setGrant(snap.exists() ? (snap.data() || {}) : null),
      () => setGrant(null)
    );
    return () => unsub();
  }, [mm?.ownerUid, mm?.managerUid]);

  useEffect(() => {
    if (mode === "overlay") {
      if (open) document.body.classList.add("sb--lock");
      else document.body.classList.remove("sb--lock");
      return () => document.body.classList.remove("sb--lock");
    }
  }, [open, mode]);

  function toggleMenu(i) {
    setOpenMenus(prev => {
      const next = {};
      if (!prev[i]) next[i] = true;
      return next;
    });
  }

  function isChildActive(children) {
    return Array.isArray(children) && children.some(c => location.pathname === c.path);
  }

  function handleLink() {
    onClose && onClose();
  }

  const computedMenu = useMemo(() => {
    const isManagerView = !!mm?.ownerUid && !!mm?.managerUid;

    const hasPersonal =
      user?.personalAccount?.active === true ||
      user?.subscriptionStatus === "active" ||
      user?.subscription?.status === "active" ||
      user?.subscription?.active === true;

    const tenantId =
      user?.businessAccount?.tenantId ||
      user?.primaryTenantId ||
      user?.tenantId ||
      "";

    const tenantRole = String(
      user?.businessAccount?.role ||
      user?.tenantRole ||
      user?.role ||
      ""
    ).toLowerCase();

    const hasBusiness =
      !!tenantId &&
      ["owner", "hr_admin", "employee"].includes(tenantRole);

    const isNursingHomeAccount =
      user?.accountKind === "nursinghome" || tenantPlanKey === "nursinghome";

    let sourceMenu = [];
    if (isNursingHomeAccount) {
      sourceMenu = [...NURSING_HOME_MENU];
    } else if (hasBusiness) {
      sourceMenu = [...BUSINESS_MENU];
    } else {
      sourceMenu = [...PERSONAL_MENU];
    }

    let baseItems = filterMenuForTenant(sourceMenu, tenantPageAccess);

    // Residents linked to a care home get a top-level "About me" item.
    // Kept out of a submenu deliberately: it is regulatory and the users are elderly.
    if (residentRefCode && !isNursingHomeAccount) {
      const homeIdx = baseItems.findIndex((it) => it && it.path === "/");
      const aboutItem = { label: "About me", path: "/about-me", icon: "personal", pageKey: "about_me" };
      if (homeIdx >= 0) {
        baseItems.splice(homeIdx + 1, 0, aboutItem);
      } else {
        baseItems.unshift(aboutItem);
      }
    }

    if (!isManagerView && tenantId && ["owner", "hr_admin"].includes(tenantRole)) {
      baseItems.unshift({
        label: tenantPlanKey === "nursinghome" ? "Nursing Home Dashboard" : "Tenant Dashboard",
        path: `/tenant/${tenantId}`,
        icon: "admin",
        pageKey: "tenant_dashboard",
      });
    }

    if (!isManagerView && user && user.isAdmin) {
      baseItems.push({
        label: "Admin",
        icon: "admin",
        children: [
          { label: "Dashboard", path: "/admin" },
          { label: "Analytics", path: "/admin/analytics" },
          { label: "Leads", path: "/admin/leads" },
          { label: "Admin Tickets", path: "/admin/support" },
        ],
      });
    }

    if (!isManagerView) return baseItems;

    const g = grant;
    const managerHeader = [
      { label: "Managed Accounts", path: "/manager-select", icon: "contacts" },
    ];

    if (isTenantAccessEnabled(tenantPageAccess, "home") && canManagerSeePage(g, "home")) {
      managerHeader.push({ label: "Home", path: "/", icon: "home", pageKey: "home" });
    }

    const withoutOwnerManagerScreens = baseItems.filter((i) => i.label !== "Account Managers" && i.label !== "Home");
    const filtered = filterMenuForManager(withoutOwnerManagerScreens, g);

    return [...managerHeader, ...filtered];
  }, [user, mm?.ownerUid, mm?.managerUid, grant, tenantPageAccess, tenantPlanKey, residentRefCode]);

  const sidebarBrandingActive = branding?.enabled === true && branding?.sidebarEnabled === true;

  const sidebarClassName = sidebarBrandingActive ? "sb-theme-custom" : "sb-theme-default";

  const sidebarVars = sidebarBrandingActive
    ? {
        "--sb-accent": branding?.colors?.accent || "#f15822",
        "--sb-accent-ink": branding?.colors?.accent || "#f15822",
        "--sb-hover-bg": branding?.colors?.secondary || "#645155",
        "--sb-active-bg": branding?.colors?.secondary || "#645155",
        "--sb-border": branding?.colors?.secondary || "#645155",
        "--sb-border-strong": branding?.colors?.accent || "#f15822",
        "--sb-overlay-tint": branding?.colors?.accent || "#f15822",
        "--sb-panel-bg": branding?.colors?.primary || "#2a0516",
        "--sb-panel-bg-solid": branding?.colors?.primary || "#2a0516",
        "--sb-link-bg": branding?.colors?.secondary || "#645155",
        "--sb-link-bg-strong": branding?.colors?.secondary || "#645155",
      }
    : undefined;

  return (
    <>
      {mode === "overlay" && open && <div className={"sb-overlay " + sidebarClassName} onClick={onClose} style={sidebarVars} />}

      {mode === "overlay" ? (
        <aside
          className={"sb-panel " + sidebarClassName + (open ? " is-open" : "")}
          role="dialog"
          aria-modal="true"
          aria-label="Main menu"
          onClick={e => e.stopPropagation()}
          style={sidebarVars}
        >
          <div className="sb-header">
            <div className="sb-brand">Menu</div>
            {onClose && <button className="sb-close" aria-label="Close menu" onClick={onClose}>×</button>}
          </div>
          <NavList
            location={location}
            openMenus={openMenus}
            toggleMenu={toggleMenu}
            isChildActive={isChildActive}
            handleLink={handleLink}
            items={computedMenu}
          />
        </aside>
      ) : (
        <aside className={"sb-perm " + sidebarClassName} aria-label="Main menu" style={sidebarVars}>
          <div className="sb-perm-header">
            <div className="sb-perm-brand">Menu</div>
          </div>
          <NavList
            location={location}
            openMenus={openMenus}
            toggleMenu={toggleMenu}
            isChildActive={isChildActive}
            handleLink={handleLink}
            items={computedMenu}
          />
        </aside>
      )}

      <style>{`
        :root{
          --sb-accent: #f15822;
          --sb-accent-ink: #f15822;
          --sb-hover-bg: rgba(0,0,0,.04);
          --sb-active-bg: rgba(241,88,34,.08);
          --sb-border: rgba(0,0,0,.07);
          --sb-border-strong: #f15822;
          --sb-overlay-tint: rgba(241,88,34,.15);
          --sb-panel-bg: #ededee;
          --sb-panel-bg-solid: #ededee;
          --sb-link-bg: transparent;
          --sb-link-bg-strong: transparent;
        }

        body.sb--lock { overflow:hidden; }

        .sb-overlay{
          position:fixed; inset:0; z-index:11000;
          background: radial-gradient(120% 120% at 90% -10%, var(--sb-overlay-tint), rgba(0,0,0,.55));
        }

        .sb-panel{
          position:fixed; top:0; left:0; height:100dvh; width:min(86vw,380px); max-width:100%;
          color:#fff; z-index:11001; transform:translateX(-100%);
          transition:transform .24s ease-out; display:flex; flex-direction:column;
          backdrop-filter: blur(18px) saturate(1.1); -webkit-backdrop-filter: blur(18px) saturate(1.1);
          border-right:1px solid var(--sb-border);
        }
        .sb-panel.is-open{ transform:translateX(0); }

        .sb-perm{
          position:sticky; top:0; height:100dvh; width:280px; flex:0 0 280px;
          color:#fff; backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
          border-right:1px solid var(--sb-border); display:flex; flex-direction:column;
        }

        .sb-theme-default.sb-panel{
          background:#ededee;
          color:#1f2937;
        }
        .sb-theme-default.sb-perm{
          background:#ededee;
          color:#1f2937;
          backdrop-filter:none;
          -webkit-backdrop-filter:none;
          box-shadow:2px 0 8px rgba(0,0,0,.06);
        }

        .sb-theme-custom.sb-panel{
          background:var(--sb-panel-bg);
        }
        .sb-theme-custom.sb-perm{
          background:linear-gradient(180deg, var(--sb-panel-bg-solid), var(--sb-panel-bg));
        }

        .sb-perm-header{ padding:14px 16px; border-bottom:1px solid var(--sb-border); }
        .sb-perm-brand{ font-weight:800; letter-spacing:.3px; }

        .sb-header{
          display:flex; align-items:center; justify-content:space-between; gap:12px;
          padding:14px 16px; border-bottom:1px solid var(--sb-border);
        }
        .sb-brand{ font-weight:800; letter-spacing:.3px; }
        .sb-close{
          width:38px; height:38px; border-radius:12px; border:1px solid rgba(255,255,255,.18);
          background:rgba(255,255,255,.08); color:#fff; cursor:pointer;
        }

        .sb-nav{ padding:12px; overflow:auto; -webkit-overflow-scrolling:touch; }
        .sb-list{ list-style:none; padding:0; margin:0; }
        .sb-li{ margin:6px 0; }

        .sb-parent{
          width:100%; display:flex; align-items:center; gap:10px; padding:12px; font-size:14px; text-align:left;
          color:#fff; border-radius:14px; cursor:pointer;
          transition: background .18s ease, border-color .18s ease, box-shadow .18s ease;
        }

        .sb-theme-default .sb-parent{
          background:transparent;
          border:1px solid transparent;
          color:#374151;
        }
        .sb-theme-default .sb-parent:hover,
        .sb-theme-default .sb-parent:focus{
          background:rgba(0,0,0,.05);
          border-color:rgba(0,0,0,.08);
        }
        .sb-theme-default .sb-parent.is-active{
          background:rgba(241,88,34,.10);
          border-color:rgba(241,88,34,.25);
          box-shadow:none;
          color:#f15822;
        }

        .sb-theme-custom .sb-parent{
          background:var(--sb-link-bg);
          border:1px solid var(--sb-border);
        }
        .sb-theme-custom .sb-parent:hover,
        .sb-theme-custom .sb-parent:focus{
          background:var(--sb-hover-bg);
          border-color:var(--sb-border-strong);
        }
        .sb-theme-custom .sb-parent.is-active{
          background:var(--sb-active-bg);
          border-color:var(--sb-border-strong);
          box-shadow: 0 0 0 2px var(--sb-accent) inset;
        }

        .sb-caret{ margin-left:auto; transition:transform .2s; opacity:.9 }
        .sb-caret.rot{ transform:rotate(90deg); }

        .sb-children{ list-style:none; padding-left:0; margin-top:4px; }

        .sb-link{
          display:flex; align-items:center; gap:10px; padding:10px 12px; color:#fff; text-decoration:none; font-size:14px;
          border-radius:12px; position:relative;
          transition: background .18s ease, border-color .18s ease, box-shadow .18s ease, transform .06s ease;
        }

        .sb-theme-default .sb-link{
          border:1px solid transparent;
          background:transparent;
          color:#4b5563;
        }
        .sb-theme-default .sb-link:hover,
        .sb-theme-default .sb-link:focus{
          background:rgba(0,0,0,.05);
          border-color:rgba(0,0,0,.08);
          box-shadow:none;
          color:#111827;
        }
        .sb-theme-default .sb-link.is-active{
          background:rgba(241,88,34,.10);
          border-color:rgba(241,88,34,.25);
          box-shadow:none;
          color:#f15822;
        }
        .sb-theme-default .sb-link.is-active::before{
          content:""; position:absolute; left:-1px; top:8px; bottom:8px; width:3px;
          background:#f15822; border-radius:2px;
        }

        .sb-theme-custom .sb-link{
          border:1px solid var(--sb-border);
          background:var(--sb-link-bg-strong);
        }
        .sb-theme-custom .sb-link:hover,
        .sb-theme-custom .sb-link:focus{
          background:var(--sb-hover-bg);
          border-color:var(--sb-border-strong);
          box-shadow: 0 0 0 2px var(--sb-accent) inset;
        }
        .sb-theme-custom .sb-link.is-active{
          background:var(--sb-active-bg);
          border-color:var(--sb-border-strong);
          box-shadow: 0 0 0 2px var(--sb-accent) inset;
        }
        .sb-theme-custom .sb-link.is-active::before{
          content:""; position:absolute; left:-1px; top:8px; bottom:8px; width:3px;
          background: var(--sb-accent); border-radius:2px;
        }

        .sb-link:active{ transform: translateY(0.5px); }

        .sb-theme-default .sb-link:hover .sb-ico,
        .sb-theme-default .sb-link.is-active .sb-ico,
        .sb-theme-default .sb-parent.is-active .sb-ico{
          color:#f15822;
        }
        .sb-theme-default .sb-close{
          background:rgba(0,0,0,.06);
          color:#374151;
          border-color:rgba(0,0,0,.12);
        }

        .sb-theme-custom .sb-link:hover .sb-ico,
        .sb-theme-custom .sb-link.is-active .sb-ico,
        .sb-theme-custom .sb-parent.is-active .sb-ico{
          color:var(--sb-accent);
        }

        .sb-ico{ width:20px; text-align:center; }
        .sb-text{ flex:1; min-width:0; word-break:break-word; }
        .sb-badge{
          margin-left:8px; font-size:11px; font-weight:700; padding:2px 6px; border-radius:10px;
          background:#ffe8f4; color:#8a1453;
        }

        .sb-link:focus-visible, .sb-parent:focus-visible{
          outline:2px solid var(--sb-border-strong); outline-offset:2px;
        }

        .sb-children .sb-link{
          padding-left:42px;
        }

        @media (max-width: 900px){
          .sb-parent{ font-size:16px; }
          .sb-link{ font-size:16px; }
          .sb-panel{ top:60px; height:calc(100dvh - 60px); }
        }

        @media (max-width: 420px){
          .sb-panel{ width:92vw; }
        }
      `}</style>
    </>
  );
}

function NavList({ location, openMenus, toggleMenu, isChildActive, handleLink, items }) {
  return (
    <nav className="sb-nav" role="navigation">
      <ul className="sb-list">
        {items.map((item, i) => (
          <li key={item.label} className="sb-li">
            {item.children ? (
              <>
                <button
                  type="button"
                  className={
                    "sb-parent" +
                    (openMenus[i] ? " is-open" : "") +
                    (isChildActive(item.children) ? " is-active" : "")
                  }
                  onClick={() => toggleMenu(i)}
                >
                  <span className="sb-ico" aria-hidden="true"><Icon name={item.icon} /></span>
                  <span className="sb-text">{item.label}</span>
                  <span className={"sb-caret" + (openMenus[i] ? " rot" : "")} aria-hidden="true">▶</span>
                </button>
                <ul className="sb-children" style={{ display: openMenus[i] || isChildActive(item.children) ? "block" : "none" }}>
                  {item.children.map(child => {
                    const active = location.pathname === child.path;
                    return (
                      <li key={child.label} className="sb-li sb-li--child">
                        <Link
                          to={child.path}
                          onClick={handleLink}
                          className={"sb-link" + (active ? " is-active" : "")}
                          aria-current={active ? "page" : undefined}
                        >
                          {child.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : (
              <Link
                to={item.path}
                onClick={handleLink}
                className={"sb-link sb-link--single" + (location.pathname === item.path ? " is-active" : "")}
                aria-current={location.pathname === item.path ? "page" : undefined}
              >
                <span className="sb-ico" aria-hidden="true"><Icon name={item.icon} /></span>
                <span className="sb-text">{item.label}</span>
              </Link>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}
